/**
 * @license
 * SPDX-License-Identifier: Apache-2.5
 *
 * Dedicated SEO Use-Case Landing Pages Data & Copywriting Engine
 * Shared across Client SPA, Dynamic SEO Meta/Schema Engine, and Express SSR.
 */

export type UseCaseSlug =
  | 'wedding-favors'
  | 'pet-magnets'
  | 'baby-milestones'
  | 'business-magnets'
  | 'memorial-magnets';

export interface UseCaseComparisonPoint {
  aspect: string;
  ordinaryAlternative: string;
  squareMagnetsAdvantage: string;
}

export interface UseCaseIdeaCard {
  number: string;
  title: string;
  subtitle: string;
  description: string;
  recommendedQty: number;
  ctaLabel: string;
  targetRoute: 'shop' | 'moments';
}

export interface UseCaseTestimonial {
  quote: string;
  author: string;
  roleOrLocation: string;
  orderDetail: string;
}

export interface UseCaseFaq {
  question: string;
  answer: string;
}

export interface UseCasePageData {
  slug: UseCaseSlug;
  navLabel: string;
  eyebrow: string;
  heroTitle: string;
  heroHighlight: string;
  heroSubtitle: string;
  primaryCtaText: string;
  secondaryCtaText: string;
  secondaryCtaRoute: 'moments' | 'shop' | 'giftcards';
  heroImage: string;
  heroImageAlt: string;
  secondaryImage: string;
  secondaryImageAlt: string;
  recommendedQuantities: number[];
  defaultCalcQuantity: number;
  seoTitle: string;
  seoDescription: string;
  seoKeywords: string;
  persuasiveHookHeading: string;
  persuasiveHookLead: string;
  persuasiveArguments: Array<{
    title: string;
    body: string;
    statValue: string;
    statLabel: string;
  }>;
  comparisonTitle: string;
  comparisonSubtitle: string;
  ordinaryLabel: string;
  comparisons: UseCaseComparisonPoint[];
  ideasHeading: string;
  ideasSubheading: string;
  ideas: UseCaseIdeaCard[];
  testimonials: UseCaseTestimonial[];
  faqs: UseCaseFaq[];
  bottomCtaHeading: string;
  bottomCtaBody: string;
}

export const USE_CASE_SLUGS: UseCaseSlug[] = [
  'wedding-favors',
  'pet-magnets',
  'baby-milestones',
  'business-magnets',
  'memorial-magnets'
];

export function isUseCaseSlug(val: string): val is UseCaseSlug {
  return USE_CASE_SLUGS.includes(val as UseCaseSlug);
}

export const USE_CASE_PAGES: Record<UseCaseSlug, UseCasePageData> = {
  'wedding-favors': {
    slug: 'wedding-favors',
    navLabel: 'Wedding Favours & Save the Dates',
    eyebrow: 'Custom Canadian Wedding Magnets • Handcrafted in Kamloops, BC',
    heroTitle: 'Wedding favours and Save-the-Dates guests',
    heroHighlight: 'actually keep on their fridge.',
    heroSubtitle:
      'Paper save-the-dates get buried in mail piles, and sugared almonds get left behind on reception tables. Turn your engagement portrait or wedding photos into handcrafted 2.5″ × 2.5″ solid steel-core magnets that live on your guests’ refrigerators for decades.',
    primaryCtaText: 'Create Your Wedding Magnets',
    secondaryCtaText: 'Wedding Collage & NFC Templates',
    secondaryCtaRoute: 'moments',
    heroImage: '/memory-magnets.webp',
    heroImageAlt: 'Custom 2.5 inch square wedding save the date and bridal favour photo magnets handcrafted in Canada',
    secondaryImage: '/handcrafted-magnets.webp',
    secondaryImageAlt: 'Solid steel core and full-coverage magnetic backing on SquareMagnets.ca custom magnets',
    recommendedQuantities: [24, 48, 75, 100, 150, 250],
    defaultCalcQuantity: 48,
    seoTitle: 'Custom Save the Date & Wedding Favor Photo Magnets Canada | SquareMagnets.ca',
    seoDescription:
      'Design custom 2.5″ × 2.5″ Save the Date magnets & wedding favour photo keepsakes handcrafted in Canada. Solid steel core, high-gloss Mylar finish, optional NFC RSVP tap, and bulk discounts up to 58% off.',
    seoKeywords:
      'save the date magnets Canada, custom wedding favor magnets, wedding photo magnets Canada, engagement photo magnets, bulk wedding favors Canada, personalized bridal shower favors, NFC save the date magnet',
    persuasiveHookHeading: 'Why settle for disposable wedding stationery when you can give a permanent heirloom?',
    persuasiveHookLead:
      'The average Canadian household opens the refrigerator 15 to 20 times every single day. When you send a custom 2.5″ × 2.5″ photo magnet, your wedding date and favourite portrait become part of your family and friends’ daily kitchen routine.',
    persuasiveArguments: [
      {
        title: 'Zero Reception Leftovers',
        body: 'Unlike candles, coasters, or novelty trinkets that 40% of guests accidentally leave on the banquet table, everybody loves taking home a high-gloss photo keepsake.',
        statValue: '100%',
        statLabel: 'Take-home guest keeper rate'
      },
      {
        title: 'Mails Easily in a Standard Card',
        body: 'Each 2.5″ × 2.5″ steel-core magnet weighs just 18 grams with smooth crimped edges—slipping effortlessly inside your Save-the-Date or Thank-You envelopes.',
        statValue: '2.5″ × 2.5″',
        statLabel: 'Signature gallery square format'
      },
      {
        title: 'Automatic Wedding Bulk Pricing',
        body: 'Whether you are hosting an intimate 24-person micro-wedding or a 250-guest reception, our tiered volume pricing drops your cost down to as low as $2.49 CAD per magnet.',
        statValue: 'Up to 58%',
        statLabel: 'Automatic volume savings in cart'
      }
    ],
    comparisonTitle: 'Paper Save-the-Dates & Flimsy Sheet Magnets vs. SquareMagnets.ca',
    comparisonSubtitle:
      'Most online print shops sell thin, bendable 20-mil vinyl sheet magnets that curl at the corners and fade in sunlight. Here is why Canadian couples choose our rigid steel-core squares:',
    ordinaryLabel: 'Paper Cards & Thin Vinyl Magnets',
    comparisons: [
      {
        aspect: 'Core Construction',
        ordinaryAlternative: 'Thin flexible rubber sheet or cardstock that bends, dog-ears, and peels over time.',
        squareMagnetsAdvantage: 'Rigid solid steel shell core pressed under high tonnage—never bends, warps, or curls.'
      },
      {
        aspect: 'Print & Surface Finish',
        ordinaryAlternative: 'Uncoated ink that smudges from kitchen humidity, fingerprints, or UV light.',
        squareMagnetsAdvantage: 'High-resolution photo print sealed under a crystal-clear, wipe-clean high-gloss Mylar shield.'
      },
      {
        aspect: 'Magnetic Holding Power',
        ordinaryAlternative: 'Weak sheet magnetism that slides down stainless doors and cannot hold an invitation.',
        squareMagnetsAdvantage: 'Heavy-duty full-back magnet that firmly anchors your wedding invitation right to the fridge.'
      },
      {
        aspect: 'Interactive RSVP Option',
        ordinaryAlternative: 'Guests have to manually type long wedding website URLs into their browser.',
        squareMagnetsAdvantage: 'Optional embedded Smart NFC chip lets guests tap their phone to open your RSVP page or engagement video.'
      }
    ],
    ideasHeading: '3 Unforgettable Ways to Use Custom Magnets for Your Wedding',
    ideasSubheading: 'Upload a single engagement portrait in our Design Studio or mix multiple photos with custom typography in Moments Creator.',
    ideas: [
      {
        number: '01',
        title: 'Save-the-Date Announcement Squares',
        subtitle: '6 to 10 Months Before the Wedding',
        description:
          'Upload your favourite engagement photo, overlay your names, wedding date, and city in 18+ curated script or serif fonts, and add an optional NFC chip that opens your wedding RSVP website with a single phone tap.',
        recommendedQty: 48,
        ctaLabel: 'Design Save-the-Dates in Studio',
        targetRoute: 'shop'
      },
      {
        number: '02',
        title: 'Reception Place-Setting & Welcome Bag Favours',
        subtitle: 'On Your Wedding Day',
        description:
          'Place a custom photo magnet at every dinner setting or inside out-of-town hotel welcome bags. Mix and match multiple engagement photos or candid moments at the exact same bulk discount rate.',
        recommendedQty: 100,
        ctaLabel: 'Start Your Bulk Wedding Order',
        targetRoute: 'shop'
      },
      {
        number: '03',
        title: 'Newlywed Thank-You & 4-Photo Collage Keepsakes',
        subtitle: 'After Your Photographer Delivers the Gallery',
        description:
          'Once your wedding gallery arrives, slip a high-gloss ceremony magnet into every Thank-You card—or build a 9-magnet 3×3 wedding grid for your own kitchen and for both sets of parents.',
        recommendedQty: 24,
        ctaLabel: 'Create Wedding Collage Magnets',
        targetRoute: 'moments'
      }
    ],
    testimonials: [
      {
        quote:
          'We ordered 90 Save-the-Date magnets with our engagement photo from Kelowna. Literally every aunt, uncle, and bridesmaid texted us a photo of it on their fridge the day the envelopes arrived. The steel core feels so much more luxurious than thin flat magnets.',
        author: ' Madison & Tyler K.',
        roleOrLocation: 'Married in Okanagan Valley, BC',
        orderDetail: '90 Save-the-Date Magnets'
      },
      {
        quote:
          'After our wedding we used our ceremony photos to make 50 Thank-You magnets for guests and a 9-piece grid for our own fridge. The print clarity on the dresses and florals is unreal.',
        author: 'Chloé & Marc-Antoine D.',
        roleOrLocation: 'Ottawa, ON',
        orderDetail: '60 Wedding Keepsake Magnets'
      }
    ],
    faqs: [
      {
        question: 'Can we order multiple different engagement or wedding photos and still get the bulk discount?',
        answer:
          'Yes! Our automatic volume discounts are calculated on the total number of magnets in your shopping cart—not per identical image. You can upload 100 completely different guest photos or 5 different engagement poses and still get the 100+ bulk tier price on every single magnet.'
      },
      {
        question: 'Can guests tap the magnet with their phone to RSVP on our wedding website?',
        answer:
          'Yes! In our Design Studio or Moments Creator, you can toggle on our Smart NFC Tag option (+$1.50 per magnet) and paste your wedding website URL (WithJoy, Zola, The Knot, or your custom domain). Guests simply tap their iPhone or Android against the magnet to open your RSVP page instantly.'
      },
      {
        question: 'How fast are wedding orders produced and shipped across Canada?',
        answer:
          'Every wedding order is handcrafted, pressed, and inspected in our Kamloops, BC studio within 1 to 2 business days, then shipped via tracked courier across Canada (typically 2 to 5 business days).'
      },
      {
        question: 'Will a 2.5″ × 2.5″ steel-core magnet fit inside a standard greeting card envelope?',
        answer:
          'Yes. Measuring 2.5 inches × 2.5 inches (63.5 mm × 63.5 mm) with smooth, rounded wrap edges, our magnets slip neatly inside standard A2, A6, A7, and square wedding invitation envelopes without tearing paper.'
      },
      {
        question: 'Can we add our names and wedding date directly onto our photo?',
        answer:
          'Absolutely. Our online Design Studio includes 18+ wedding-ready script, serif, and modern fonts with adjustable sizing, colour pickers, and live 3D preview so you can position your names and date with pinpoint accuracy.'
      }
    ],
    bottomCtaHeading: 'Ready to create wedding keepsakes your guests will treasure for decades?',
    bottomCtaBody:
      'Upload your engagement or wedding photos now, preview your 2.5″ × 2.5″ magnets in real-time 3D, and unlock automatic volume discounts in your cart.'
  },

  'pet-magnets': {
    slug: 'pet-magnets',
    navLabel: 'Custom Pet Magnets',
    eyebrow: 'Custom Dog, Cat & Pet Photo Magnets • Made in Canada',
    heroTitle: 'Your camera roll has 4,000 photos of your pet.',
    heroHighlight: 'Put the best ones on your fridge.',
    heroSubtitle:
      'From goofy puppy ears and sleepy cat stretches to adoption-day portraits and beloved rainbow-bridge tributes—transform your favourite pet photos into vivid, high-gloss 2.5″ × 2.5″ steel-core magnets handcrafted in Kamloops, BC.',
    primaryCtaText: 'Create Your Pet Magnets Now',
    secondaryCtaText: 'Multi-Photo Pet Collages',
    secondaryCtaRoute: 'moments',
    heroImage: '/lifestyle-hero-square.png',
    heroImageAlt: 'Custom 2.5 inch square pet and family photo magnets displayed on a refrigerator door',
    secondaryImage: '/custom-magnets.webp',
    secondaryImageAlt: 'High-gloss custom 2.5x2.5 square photo magnets handcrafted in Canada',
    recommendedQuantities: [6, 9, 12, 18, 24, 48],
    defaultCalcQuantity: 9,
    seoTitle: 'Custom Dog & Cat Photo Magnets Canada | Personalized Pet Fridge Magnets | SquareMagnets.ca',
    seoDescription:
      'Turn your favourite dog, cat, or horse photos into handcrafted 2.5″ × 2.5″ high-gloss square fridge magnets in Canada. Solid steel core, vivid fur & eye detail, bulk discounts from $2.49 CAD.',
    seoKeywords:
      'custom pet magnets Canada, custom dog photo magnets, custom cat fridge magnets, personalized pet memorial magnet, dog mom gift Canada, pet portrait magnet, custom puppy photo gift',
    persuasiveHookHeading: 'Why every pet parent in Canada is turning their phone gallery into a fridge gallery',
    persuasiveHookLead:
      'Our pets give us a thousand tiny moments of joy every week—the head tilt, the snowy nose, the sunny window nap—yet 99% of those photos stay buried deep inside a smartphone screen. Physical 2.5″ × 2.5″ square magnets bring their personality into the heart of your home.',
    persuasiveArguments: [
      {
        title: 'True-to-Life Fur & Eye Detail',
        body: 'Black dogs and dark tabby cats often look muddy on cheap prints. Our high-resolution photo printing and studio colour calibration preserve every whisker and catchlight.',
        statValue: 'Crisp HD',
        statLabel: 'High-resolution photo print'
      },
      {
        title: 'Wipe-Clean Kitchen Durability',
        body: 'Protected by a thick, crystal-clear Mylar shield over a solid steel core, your pet magnets shrug off kitchen splashes, wet noses, and years of daily handling.',
        statValue: 'Steel + Mylar',
        statLabel: 'Rigid waterproof surface protection'
      },
      {
        title: 'The #1 Gift for Pet Lovers',
        body: 'Stuck on what to buy a dog mom, cat dad, or pet sitter? A 6-pack or 9-pack of custom magnets featuring their furry best friend is personal, affordable, and guaranteed to make them smile.',
        statValue: '4.95 ★',
        statLabel: 'Rated by Canadian customers'
      }
    ],
    comparisonTitle: 'Why Pet Photos Look Better on Our 2.5″ Steel-Core Squares',
    comparisonSubtitle:
      'When you print a pet portrait, contrast and surface gloss make all the difference between a flat image and a lifelike keepsake.',
    ordinaryLabel: 'Drugstore Prints & Cheap Flat Magnets',
    comparisons: [
      {
        aspect: 'Dark Fur & Shadow Detail',
        ordinaryAlternative: 'Low-contrast thermal printers turn black labs and tuxedo cats into dark, featureless silhouettes.',
        squareMagnetsAdvantage: 'High-definition lustre printing keeps rich shadow detail, bright eyes, and individual fur textures crisp.'
      },
      {
        aspect: 'Edge-to-Edge Presence',
        ordinaryAlternative: 'Die-cut novelty shapes or thin flexible magnets that curl and look like freebies.',
        squareMagnetsAdvantage: 'Gallery-style 2.5″ × 2.5″ square with precision-wrapped steel edges and 3D depth on your fridge.'
      },
      {
        aspect: 'Custom Names & Gotcha Dates',
        ordinaryAlternative: 'Fixed templates that force awkward borders over your pet’s ears or tail.',
        squareMagnetsAdvantage: 'Interactive zoom/crop canvas plus optional custom captions for their name, nickname, or Gotcha Day.'
      },
      {
        aspect: 'Mix & Match Freedom',
        ordinaryAlternative: 'Forces you to buy 10 copies of the exact same photo to get a discount.',
        squareMagnetsAdvantage: 'Upload 9 different goofy faces of your dog or cat and unlock the 9-pack discount automatically.'
      }
    ],
    ideasHeading: '3 Crowd-Favourite Ways to Print Your Pet Magnets',
    ideasSubheading: 'Mix different photos in a single order—every magnet in your cart counts toward your volume discount.',
    ideas: [
      {
        number: '01',
        title: 'The "Puppy / Kitten to Grown" 9-Square Grid',
        subtitle: 'Most Popular • 3×3 Fridge Gallery',
        description:
          'Pick 9 favourite snapshots from the day you brought them home to today—sleepy puppy paws, first snow day, birthday bandana, and trail adventures—arranged in a clean 3×3 square grid on your fridge.',
        recommendedQty: 9,
        ctaLabel: 'Create a 9-Magnet Pet Set',
        targetRoute: 'shop'
      },
      {
        number: '02',
        title: 'Surprise Gift Set for Dog Moms & Cat Dads',
        subtitle: 'Birthdays, Christmas & Stocking Stuffers',
        description:
          'Grab 6 or 12 favourite photos straight from a friend or family member’s Instagram or camera roll and turn them into a gift-ready custom magnet pack—or send a Digital Gift Card so they can pick their own.',
        recommendedQty: 12,
        ctaLabel: 'Design a Pet Lover Gift Pack',
        targetRoute: 'shop'
      },
      {
        number: '03',
        title: 'Rainbow Bridge & Beloved Pet Tribute Squares',
        subtitle: 'Honouring a Lifelong Companion',
        description:
          'Keep their happiest smile right where the family gathers every day. Add their name, years loved, or an optional Smart NFC tag that plays a favourite video clip of their tail wagging or purring when tapped.',
        recommendedQty: 6,
        ctaLabel: 'Create a Pet Tribute Magnet',
        targetRoute: 'moments'
      }
    ],
    testimonials: [
      {
        quote:
          'I have a black Newfoundland and it is usually impossible to get prints where you can actually see his eyes. These magnets came out so bright, glossy, and sharp! I ordered 9 different photos from our hikes around BC.',
        author: 'Jenna M.',
        roleOrLocation: 'Kamloops, BC',
        orderDetail: '9 Custom Dog Photo Magnets'
      },
      {
        quote:
          'Bought a set of 12 cat magnets for my sister for her birthday using photos from her Instagram. She cried happy tears and immediately arranged them on her stainless fridge.',
        author: 'Devon R.',
        roleOrLocation: 'Calgary, AB',
        orderDetail: '12 Custom Cat Magnets'
      }
    ],
    faqs: [
      {
        question: 'Can I upload 6, 9, or 12 completely different photos of my pet in one order?',
        answer:
          'Yes! In our Design Studio you can upload multiple photos at once from your phone or computer, crop each one individually, and add all of them to your cart in a single batch. Volume discounts apply to your total magnet count.'
      },
      {
        question: 'My dog or cat has very dark fur—will the photo print too dark?',
        answer:
          'Our high-resolution photo printing preserves natural mid-tones and eye catchlights. If your original phone photo was taken in dim indoor lighting, you can also use the brightness/filter controls right inside our Design Studio before adding to cart.'
      },
      {
        question: 'Can I add my pet’s name or "Gotcha Day" text onto the magnet?',
        answer:
          'Yes! You can add custom text in 18+ fonts, choose any colour, and drag the caption anywhere on the magnet—or leave your photo 100% full-bleed edge-to-edge.'
      },
      {
        question: 'Can I embed a video of my pet barking, purring, or playing into the magnet?',
        answer:
          'Yes! By selecting our Smart NFC option (+$1.50) in the Studio or Moments Creator, you can link a video URL (iCloud, Google Photos, YouTube, TikTok, or Instagram Reel) so tapping a phone to the magnet plays your pet’s video instantly.'
      },
      {
        question: 'How long does shipping take in Canada?',
        answer:
          'All pet magnets are handcrafted in Kamloops, BC within 1–2 business days and shipped coast-to-coast via tracked parcel delivery.'
      }
    ],
    bottomCtaHeading: 'Bring your best friend’s happiest moments out of your camera roll today.',
    bottomCtaBody:
      'Upload your favourite pet photos in seconds right from your phone, preview them in 3D, and enjoy automatic bulk discounts.'
  },

  'baby-milestones': {
    slug: 'baby-milestones',
    navLabel: 'Baby Milestones & Family',
    eyebrow: 'Custom Baby First Year, Ultrasound & Family Photo Magnets',
    heroTitle: 'They grow up in the blink of an eye.',
    heroHighlight: 'Freeze every milestone in 2.5″ squares.',
    heroSubtitle:
      'From the first ultrasound strip and hospital bassinet photo to Months 1 through 12, first steps, and messy birthday cake smashes—turn your baby and family photos into heirloom-quality steel-core magnets that grandparents and parents cherish every day.',
    primaryCtaText: 'Create Baby & Family Magnets',
    secondaryCtaText: 'Open Moments Collage Creator',
    secondaryCtaRoute: 'moments',
    heroImage: '/custom-magnets.webp',
    heroImageAlt: 'Custom baby first birthday, ultrasound, graduation, and family keepsake square magnets on a stainless steel fridge',
    secondaryImage: '/lifestyle-hero-square.png',
    secondaryImageAlt: 'Hand placing a baby first birthday photo magnet onto a kitchen refrigerator',
    recommendedQuantities: [6, 12, 18, 24, 48],
    defaultCalcQuantity: 12,
    seoTitle: 'Custom Baby First Year, Ultrasound & Family Photo Magnets Canada | SquareMagnets.ca',
    seoDescription:
      'Create custom 2.5″ × 2.5″ baby milestone, pregnancy ultrasound, newborn announcement, and family photo magnets in Canada. Wipe-clean Mylar shield, solid steel core, and automatic 12-pack savings.',
    seoKeywords:
      'baby milestone magnets Canada, custom ultrasound photo magnet, baby first year photo magnets, newborn announcement magnets, grandparent photo gifts Canada, family fridge magnets, custom baby shower favors',
    persuasiveHookHeading: 'Why thermal ultrasound paper fades—and why your baby’s first year deserves permanent steel-core keepsakes',
    persuasiveHookLead:
      'Did you know hospital ultrasound printouts are printed on heat-sensitive thermal paper that fades to black or blank white within 2 to 5 years? Scanning or photographing that ultrasound and pressing it into a sealed high-gloss Mylar magnet preserves it forever.',
    persuasiveArguments: [
      {
        title: 'Save Thermal Ultrasounds Forever',
        body: 'Thermal clinic paper degrades from light and humidity. Pressing your ultrasound photo under our UV-resistant Mylar shield locks in every tiny detail permanently.',
        statValue: 'Permanent',
        statLabel: 'High-gloss Mylar seal'
      },
      {
        title: 'Built for Busy Family Kitchens',
        body: 'Little fingers love rearranging magnets on the lower fridge door. Our smooth crimped steel edges have zero sharp corners, zero glass to shatter, and wipe clean in seconds.',
        statValue: 'Kid-Safe',
        statLabel: 'Shatterproof steel core build'
      },
      {
        title: 'Perfect 12-Month Grid Format',
        body: 'Our 12-magnet bulk tier ($4.99/magnet) is tailor-made for a complete Month 1 to Month 12 First-Year growth grid—or extra sets for Grandma and Grandpa’s fridge.',
        statValue: '12-Pack Tier',
        statLabel: 'Ideal for Months 1–12 milestones'
      }
    ],
    comparisonTitle: 'Why Parents & Grandparents Love Our 2.5″ Keepsake Squares',
    comparisonSubtitle:
      'Digital photo albums rarely get opened, and paper birth announcements eventually go into a drawer. Fridge magnets stay at eye level every single day.',
    ordinaryLabel: 'Paper Announcements & Thermal Prints',
    comparisons: [
      {
        aspect: 'Ultrasound Longevity',
        ordinaryAlternative: 'Original clinic thermal strips fade, crease, or darken if taped to a refrigerator.',
        squareMagnetsAdvantage: 'High-contrast photo print sealed beneath protective Mylar that stays crisp and vivid.'
      },
      {
        aspect: 'Safety Around Toddlers',
        ordinaryAlternative: 'Fragile glass frames or brittle plastic blocks that crack if dropped on tile floors.',
        squareMagnetsAdvantage: 'Unbreakable solid steel shell with smooth wrapped edges and full-coverage magnetic backing.'
      },
      {
        aspect: 'Month-by-Month Storytelling',
        ordinaryAlternative: 'Photos scattered across thousands of phone screenshots that never get printed.',
        squareMagnetsAdvantage: 'Uniform 2.5″ × 2.5″ squares that line up in a neat 3×4 twelve-month grid on any fridge.'
      },
      {
        aspect: 'Audio/Video First Laughs',
        ordinaryAlternative: 'Static paper cards that cannot capture the sound of baby’s first giggle.',
        squareMagnetsAdvantage: 'Optional Smart NFC chip lets grandparents tap their phone to hear baby’s laugh or watch first steps.'
      }
    ],
    ideasHeading: '3 Heartwarming Baby & Family Magnet Ideas',
    ideasSubheading: 'Upload photos straight from your smartphone camera roll in under 2 minutes.',
    ideas: [
      {
        number: '01',
        title: 'The "Months 1 to 12" First Year Growth Grid',
        subtitle: '12 Magnets • Complete First-Year Timeline',
        description:
          'Upload your monthly milestone blanket or onesie photos from Month 1 through First Birthday Cake Smash. Add "1 Month", "2 Months"... captions in the Studio or keep them clean and full-bleed.',
        recommendedQty: 12,
        ctaLabel: 'Build Your 12-Month Baby Set',
        targetRoute: 'shop'
      },
      {
        number: '02',
        title: 'Ultrasound Reveal & Birth Announcement Magnets',
        subtitle: 'For Grandparents, Aunts, Uncles & Godparents',
        description:
          'Snap a clear photo of your ultrasound strip or newborn hospital portrait, add baby’s name, birth date, weight, and time of birth, and mail a permanent keepsake to the whole family.',
        recommendedQty: 18,
        ctaLabel: 'Design Ultrasound & Birth Magnets',
        targetRoute: 'shop'
      },
      {
        number: '03',
        title: 'Baby Shower Favours & Multi-Generation Collages',
        subtitle: 'Moments Collage Creator & NFC Keepsakes',
        description:
          'Thank baby shower guests with a custom favour magnet, or combine 4 family photos onto a single Moments collage square for Mother’s Day, Father’s Day, or Grandparents’ Day.',
        recommendedQty: 24,
        ctaLabel: 'Open Moments Collage Creator',
        targetRoute: 'moments'
      }
    ],
    testimonials: [
      {
        quote:
          'We turned our 20-week ultrasound and our daughter’s first 11 monthly photos into a 12-magnet grid on our fridge, plus ordered duplicate sets for both grandmas. The quality is incredible and our toddler loves pointing at "baby me" every morning!',
        author: 'Sarah & Liam T.',
        roleOrLocation: 'Vancouver, BC',
        orderDetail: '36 Baby Milestone & Grandparent Magnets'
      },
      {
        quote:
          'My original ultrasound from my first son had already started fading in his baby book, so I photographed both boys’ ultrasounds and had them made into magnets here. They look crisp and brand new.',
        author: 'Kelsey B.',
        roleOrLocation: 'Edmonton, AB',
        orderDetail: '6 Ultrasound & Newborn Magnets'
      }
    ],
    faqs: [
      {
        question: 'How do I turn my paper ultrasound printout into a magnet?',
        answer:
          'Simply lay your ultrasound printout flat in bright, even natural light (avoiding overhead glare) and take a straight-on photo with your smartphone, or use a scanner. Upload that image into our Design Studio, crop to the baby profile, and we handle the rest!'
      },
      {
        question: 'Are your magnets safe if my toddler pulls them off the bottom of the fridge?',
        answer:
          'Our 2.5″ × 2.5″ magnets are large enough that they are not a small-parts swallowing hazard (63.5 mm × 63.5 mm), and they are made with a shatterproof steel core and smooth Mylar-wrapped edges with zero glass or brittle plastic.'
      },
      {
        question: 'Can I ship a gift order directly to Grandma and Grandpa with a gift note?',
        answer:
          'Yes! During checkout, simply check the "This order is a gift" box to include a complimentary custom gift presentation card with your To, From, and personal message—and we will never include pricing on the packing slip.'
      },
      {
        question: 'Can I add each month number ("Month 1", "Month 2") onto the photos?',
        answer:
          'Yes! Each photo in your batch can have its own custom text overlay, font, and colour right inside our Design Studio.'
      },
      {
        question: 'What if I want to let new parents choose their own baby photos?',
        answer:
          'Our Digital Gift Cards (starting at $30 CAD) are one of our most popular baby shower and new-parent gifts—delivered instantly by email with a never-expiring code so parents can order as baby grows.'
      }
    ],
    bottomCtaHeading: 'Preserve your family’s sweetest milestones before they grow another inch.',
    bottomCtaBody:
      'Upload ultrasounds, newborn portraits, and family memories straight from your phone and preview your 2.5″ squares in 3D.'
  },

  'business-magnets': {
    slug: 'business-magnets',
    navLabel: 'Business & QR Promo Magnets',
    eyebrow: 'Custom Promotional, Logo & QR/NFC Business Magnets in Bulk',
    heroTitle: '88% of paper business cards are thrown out in a week.',
    heroHighlight: 'Own the kitchen fridge instead.',
    heroSubtitle:
      'When a pipe bursts, an appliance breaks, or it is time to reorder, homeowners do not search through a junk drawer—they look at the refrigerator door. Put your logo, phone number, scannable QR code, or tap-to-book Smart NFC chip on a heavy-duty 2.5″ × 2.5″ steel-core magnet starting at just $2.49 CAD in bulk.',
    primaryCtaText: 'Upload Your Logo & QR Design',
    secondaryCtaText: 'Smart NFC Business Magnets',
    secondaryCtaRoute: 'moments',
    heroImage: '/square-business-magnets.webp',
    heroImageAlt: 'Custom 2.5 inch square promotional business logo and QR code magnets handcrafted in Kamloops BC Canada',
    secondaryImage: '/handcrafted-magnets.webp',
    secondaryImageAlt: 'Back and front of a handcrafted SquareMagnets.ca QR code business magnet showing solid steel core and heavy-duty magnetic backing',
    recommendedQuantities: [24, 48, 100, 250, 500],
    defaultCalcQuantity: 250,
    seoTitle: 'Custom Promotional & QR/NFC Business Magnets in Bulk Canada | SquareMagnets.ca',
    seoDescription:
      'Order heavy-duty 2.5″ × 2.5″ custom business logo, QR code, and Smart NFC promotional magnets in bulk from $2.49 CAD. Solid steel core, high-gloss Mylar finish, handcrafted in Canada with fast tracked shipping.',
    seoKeywords:
      'custom business magnets Canada, promotional magnets in bulk Canada, QR code business magnets, custom logo fridge magnets, real estate closing gift magnets, HVAC plumber service magnets, NFC Google review magnet',
    persuasiveHookHeading: 'The highest-ROI local marketing asset per dollar spent in Canada',
    persuasiveHookLead:
      'A single Google Ads click for a plumber, realtor, HVAC tech, or local service business can cost $15 to $45 CAD—and disappears in one second. A handcrafted 2.5″ × 2.5″ steel-core magnet costs as little as $2.49 CAD in bulk and generates thousands of household impressions over 5+ years.',
    persuasiveArguments: [
      {
        title: '5,000+ Yearly Impressions Per Home',
        body: 'Placed on a customer’s refrigerator, electrical panel, furnace, or office whiteboard, your brand is seen every single day by everyone in the home.',
        statValue: '$0.0005',
        statLabel: 'Cost per household impression'
      },
      {
        title: 'Crisp Scannable QR + Optional NFC',
        body: 'Our high-resolution printing guarantees instant smartphone camera QR scanning to your booking page, menu, or Google Review link—plus optional NFC tap.',
        statValue: '1-Tap / Scan',
        statLabel: 'Instant booking & review conversion'
      },
      {
        title: 'Feels Like Merchandise, Not Junk Mail',
        body: 'Cheap thin vinyl sheet magnets scream "junk mail" and get tossed. Our rigid steel-core button-style square magnets feel substantial, premium, and stay put.',
        statValue: '$2.49 CAD',
        statLabel: 'Wholesale 250+ unit rate'
      }
    ],
    comparisonTitle: 'Why Canadian Businesses Are Ditching Flimsy Vinyl Magnets & Paper Cards',
    comparisonSubtitle:
      'Your promotional magnet represents the quality of your workmanship. If it curls, peels, or slides down the fridge, it hurts your brand.',
    ordinaryLabel: 'Paper Cards & Thin 20-Mil Vinyl Magnets',
    comparisons: [
      {
        aspect: 'Perceived Brand Value',
        ordinaryAlternative: 'Thin, floppy sheet magnets feel like cheap flyers and often go straight into the recycling bin.',
        squareMagnetsAdvantage: 'Rigid 3D steel-core square with high-gloss Mylar finish feels like a premium brand gift.'
      },
      {
        aspect: 'QR Code Scan Reliability',
        ordinaryAlternative: 'Low-DPI screen printing blurs QR modules and fails to scan under kitchen lighting.',
        squareMagnetsAdvantage: 'Photographic pigment printing renders razor-sharp QR codes that scan in 0.2 seconds.'
      },
      {
        aspect: 'Furnace, Panel & Fridge Hold',
        ordinaryAlternative: 'Weak magnetic sheeting slides off brushed stainless steel or vibrating HVAC ductwork.',
        squareMagnetsAdvantage: 'Heavy-duty full-back magnet grips tightly to fridges, furnaces, water heaters, and steel toolboxes.'
      },
      {
        aspect: 'Canadian Turnaround & Billing',
        ordinaryAlternative: 'Weeks of cross-border customs delays, USD exchange fees, and surprise brokerage charges.',
        squareMagnetsAdvantage: '100% Canadian manufactured in Kamloops, BC. Priced in CAD with downloadable GST/receipt invoices.'
      }
    ],
    ideasHeading: '3 Proven Ways Local & National Brands Use Our 2.5″ Business Magnets',
    ideasSubheading: 'Every business order includes downloadable PDF paid invoices for 100% business expense write-offs.',
    ideas: [
      {
        number: '01',
        title: 'QR Code Service & Emergency Dispatch Magnets',
        subtitle: 'HVAC, Plumbing, Electrical, Appliance Repair & Vet Clinics',
        description:
          'Leave a steel-core magnet on the customer’s fridge, furnace, or electrical panel after every service call. Include your logo, 24/7 phone number, and a QR code that opens your online booking form.',
        recommendedQty: 250,
        ctaLabel: 'Upload Your Business Card / QR Artwork',
        targetRoute: 'shop'
      },
      {
        number: '02',
        title: 'Real Estate Closing Gifts & Client Appreciation',
        subtitle: 'Realtors, Mortgage Brokers, Photographers & Gift Baskets',
        description:
          'Upload a photo of your buyers in front of their new home with the "Sold" sign—or include a branded magnet in every client gift basket—so you are the first call when their friends ask for a referral.',
        recommendedQty: 48,
        ctaLabel: 'Create Client Keepsake Magnets',
        targetRoute: 'shop'
      },
      {
        number: '03',
        title: 'Smart NFC "Tap to Leave a 5-Star Google Review" Magnets',
        subtitle: 'Restaurants, Salons, Retail Counters & Trade Shows',
        description:
          'Embed a Smart NFC chip programmed with your direct Google Review link or Linktree. Customers simply tap their phone to the magnet at your checkout counter or at home to leave a 5-star review.',
        recommendedQty: 24,
        ctaLabel: 'Build Smart NFC Business Magnets',
        targetRoute: 'moments'
      }
    ],
    testimonials: [
      {
        quote:
          'We switched from flat vinyl magnets to SquareMagnets.ca for our appliance and mattress customers. The QR code scans instantly and customers actually put these right on the front of their fridge because they look so sharp.',
        author: 'Local Business Partner',
        roleOrLocation: 'Kamloops, BC',
        orderDetail: '250+ Custom QR Code Business Magnets'
      },
      {
        quote:
          'For every home closing, I order 6 magnets featuring the family’s photo in front of their new front door plus one magnet with my brokerage contact QR code. Best $35 closing gift touch I have ever added.',
        author: 'jordan V., REALTOR®',
        roleOrLocation: 'Lower Mainland, BC',
        orderDetail: 'Recurring Client Closing Gift Orders'
      }
    ],
    faqs: [
      {
        question: 'What is the bulk price for 250+ custom business magnets?',
        answer:
          'Our 250+ wholesale tier drops the price all the way down to $2.49 CAD per magnet (a 58% savings off our single-unit price), with 100% free tracked shipping across Canada automatically included.'
      },
      {
        question: 'Will a QR code printed on the magnet scan reliably with iPhone and Android cameras?',
        answer:
          'Yes! Because we use high-resolution photo printing rather than low-DPI screen printing, QR codes render with razor-sharp contrast. We recommend leaving a clean white quiet zone around your QR code and keeping it inside the 2.5″ × 2.5″ front face guide in our Studio.'
      },
      {
        question: 'Can I download an official paid PDF invoice for my company accounting?',
        answer:
          'Yes. Immediately after checkout, you can download a complete Paid Invoice & Receipt PDF directly from your Order Tracking page for your bookkeeper or tax records.'
      },
      {
        question: 'Can we program NFC business magnets to open our Google Review link or website?',
        answer:
          'Yes! Toggle on the Smart NFC option (+$1.50/magnet) and paste your Google Business Review link, online booking URL, digital menu, or vCard link. Anyone who taps their smartphone to the magnet is taken straight to your URL.'
      },
      {
        question: 'Can I preview the exact 2.5″ front face and wrapped edge bleed before ordering?',
        answer:
          'Yes! Our online Design Studio shows you the exact 2.5″ × 2.5″ finished face boundary as well as a 3D rotatable preview and Full Print Sheet bleed proof so your logo and phone number are framed with 100% precision.'
      }
    ],
    bottomCtaHeading: 'Put your business on every customer’s fridge for as little as $2.49 CAD per home.',
    bottomCtaBody:
      'Upload your logo or QR code layout in our Design Studio, preview the finished 2.5″ face in 3D, and unlock automatic wholesale volume pricing.'
  },

  'memorial-magnets': {
    slug: 'memorial-magnets',
    navLabel: 'Celebration of Life & Memorials',
    eyebrow: 'Custom Celebration of Life & Memorial Keepsake Photo Magnets',
    heroTitle: 'A gentle, lasting tribute to keep their smile',
    heroHighlight: 'close every single day.',
    heroSubtitle:
      'Paper funeral programs get tucked away in drawers, and cut flowers fade within days. Honour a loved one’s life with a handcrafted 2.5″ × 2.5″ high-gloss photo magnet for family and Celebration of Life guests—so their warmest smile stays right in the heart of every home.',
    primaryCtaText: 'Create Memorial Keepsake Magnets',
    secondaryCtaText: 'Collage & NFC Tribute Creator',
    secondaryCtaRoute: 'moments',
    heroImage: '/memory-magnets.webp',
    heroImageAlt: 'Handcrafted 2.5 inch square memorial and family keepsake photo magnets made in Canada',
    secondaryImage: '/handcrafted-magnets.webp',
    secondaryImageAlt: 'Handcrafted solid steel-core keepsake magnet construction from SquareMagnets.ca',
    recommendedQuantities: [12, 24, 48, 75, 100, 150],
    defaultCalcQuantity: 48,
    seoTitle: 'Custom Celebration of Life & Memorial Keepsake Photo Magnets Canada | SquareMagnets.ca',
    seoDescription:
      'Honour a loved one with custom 2.5″ × 2.5″ Celebration of Life and memorial keepsake photo magnets handcrafted in Canada. Vivid photo print, protective Mylar finish, optional NFC voice/video memory tag, and fast 1–2 day rush production.',
    seoKeywords:
      'celebration of life favors Canada, memorial photo magnets Canada, in loving memory magnets, funeral keepsake gifts for guests, custom remembrance photo magnet, celebration of life keepsakes BC',
    persuasiveHookHeading: 'Why families across Canada choose photo magnets for Celebration of Life services',
    persuasiveHookLead:
      'When friends and extended family gather to celebrate a life well lived, they want something meaningful to take home—not a formal paper pamphlet that feels too heavy to display, but a warm, joyful portrait they can see every morning while making coffee.',
    persuasiveArguments: [
      {
        title: 'Prioritized 1–2 Day Studio Turnaround',
        body: 'We know Celebration of Life services often come together on short timelines. Every order is handcrafted in Kamloops, BC within 1 to 2 business days with tracked shipping.',
        statValue: '1–2 Days',
        statLabel: 'Fast Canadian studio production'
      },
      {
        title: 'Restores Vintage & Scanned Photos',
        body: 'Whether you are using a recent smartphone portrait or a scanned 1970s film photograph, our high-resolution printing and subtle Studio filters bring warmth and clarity to every face.',
        statValue: 'Crisp HD',
        statLabel: 'Vivid high-resolution photo print'
      },
      {
        title: 'Optional NFC Voice & Video Tribute',
        body: 'Embed a Smart NFC chip that links to an online memorial page, slideshow video, or a recorded voice message so family can tap their phone and hear their voice anytime.',
        statValue: 'Smart NFC',
        statLabel: 'Tap-to-play slideshow or audio'
      }
    ],
    comparisonTitle: 'A Meaningful Alternative to Traditional Funeral Bookmarks & Paper Programs',
    comparisonSubtitle:
      'Give family members, pallbearers, and guests a tribute that brings comfort in everyday life for decades to come.',
    ordinaryLabel: 'Paper Programs & Laminated Bookmarks',
    comparisons: [
      {
        aspect: 'Everyday Presence',
        ordinaryAlternative: 'Paper cards and bookmarks are placed inside a drawer or bookshelf and rarely seen again.',
        squareMagnetsAdvantage: 'Lives right on the refrigerator door or office board where family sees their smile daily.'
      },
      {
        aspect: 'Durability Over Decades',
        ordinaryAlternative: 'Cardstock bends, yellows, or gets damaged by moisture over the years.',
        squareMagnetsAdvantage: 'Solid steel core sealed under protective high-gloss Mylar that stays pristine for generations.'
      },
      {
        aspect: 'Multiple Eras of Their Life',
        ordinaryAlternative: 'Standard print runs charge extra setup fees if you want more than one photo.',
        squareMagnetsAdvantage: 'Mix childhood, career, hobbies, and family photos in one order at the exact same bulk rate.'
      },
      {
        aspect: 'Interactive Memory Sharing',
        ordinaryAlternative: 'Limited to a few lines of printed text on paper.',
        squareMagnetsAdvantage: 'Optional NFC tag lets guests tap the magnet to view the full tribute video or guestbook.'
      }
    ],
    ideasHeading: '3 Thoughtful Ways to Create Celebration of Life & Tribute Magnets',
    ideasSubheading: 'Add custom text ("Forever in Our Hearts", name, and dates) or let a favourite photograph speak for itself.',
    ideas: [
      {
        number: '01',
        title: 'Celebration of Life Guest Keepsake Table',
        subtitle: '48 to 150 Magnets • Reception & Service Favours',
        description:
          'Place a basket or display board of 2.5″ × 2.5″ magnets next to the guestbook so every attendee can take home a favourite photo. Mix 3 or 4 different photos from different decades of their life so guests can choose their favourite memory.',
        recommendedQty: 48,
        ctaLabel: 'Create Celebration of Life Magnets',
        targetRoute: 'shop'
      },
      {
        number: '02',
        title: 'Multi-Photo Life Story Collage Squares',
        subtitle: 'Moments Creator • 2 to 4 Photos Per Magnet',
        description:
          'Combine a youthful portrait alongside a recent family photo, or include their name, birth/passing years, and a favourite short quote using our Moments Creator layouts.',
        recommendedQty: 24,
        ctaLabel: 'Open Moments Tribute Creator',
        targetRoute: 'moments'
      },
      {
        number: '03',
        title: 'Smart NFC Video & Voice Memory Keepsakes',
        subtitle: 'For Immediate Family, Children & Grandchildren',
        description:
          'Add a Smart NFC tag (+$1.50) linked to the Celebration of Life video tribute, an online obituary guestbook, or a cherished family video clip so loved ones can tap their phone anytime.',
        recommendedQty: 12,
        ctaLabel: 'Design NFC Memory Magnets',
        targetRoute: 'moments'
      }
    ],
    testimonials: [
      {
        quote:
          'For my dad’s Celebration of Life, we ordered 75 magnets using three different photos—him fishing on Shuswap Lake, in his workshop, and laughing with his grandkids. Everyone at the service took one home and told us it was the most special tribute they had ever seen.',
        author: 'greg & Elaine H.',
        roleOrLocation: 'Salmon Arm, BC',
        orderDetail: '75 Celebration of Life Magnets'
      },
      {
        quote:
          'Thank you for getting our order pressed and shipped out the very next morning so it arrived in time for my grandmother’s service in Winnipeg. Seeing her smile on our fridge every morning brings us so much comfort.',
        author: 'Rachelle S.',
        roleOrLocation: 'Winnipeg, MB',
        orderDetail: '48 Memorial Keepsake Magnets'
      }
    ],
    faqs: [
      {
        question: 'Our Celebration of Life service is coming up soon—how fast do you ship?',
        answer:
          'We prioritize memorial and Celebration of Life orders in our Kamloops, BC studio, pressing and dispatching within 1 to 2 business days with tracked shipping across Canada. You can also leave your service date in the "Special Instructions" box at checkout.'
      },
      {
        question: 'Can we include 3 or 4 different photos from throughout their life in one bulk order?',
        answer:
          'Yes! Many families upload 3 to 5 different photos (for example: a young portrait, a hobby/outdoor photo, and a recent family portrait) so guests at the service can pick the photo that means the most to them. All magnets in your cart combine for the bulk discount.'
      },
      {
        question: 'Can we add "In Loving Memory", their name, and dates onto the magnet?',
        answer:
          'Yes. Our Design Studio lets you add custom text lines in classic serif, script, or clean sans-serif typography, adjust text size and colour, and position it cleanly at the top or bottom of the portrait.'
      },
      {
        question: 'Can I use an older printed photograph that I don’t have a digital file for?',
        answer:
          'Yes! Simply place the printed photograph near a window with soft, even daylight (avoiding direct flash glare) and take a close-up photo with your smartphone, then upload it directly into our Design Studio.'
      },
      {
        question: 'How does the Smart NFC video/tribute link work on memorial magnets?',
        answer:
          'When you enable Smart NFC (+$1.50/magnet), we embed a thin, invisible NFC microchip inside the magnet programmed to your tribute video URL (YouTube, Vimeo, Google Drive, or online obituary page). Family members simply tap the top of their smartphone to the magnet to open the tribute.'
      }
    ],
    bottomCtaHeading: 'Create a warm, lasting tribute your family and friends will hold onto forever.',
    bottomCtaBody:
      'Upload cherished portraits in our Design Studio, add names or dates with live 3D preview, and receive fast 1–2 day handcrafted production from our Canadian studio.'
  }
};
